"use client";

/**
 * Enhanced Authentication Context
 * Manages authentication state and integrates with backend API
 * Syncs Privy identity tokens to axios headers for secure API calls
 * Based on Spicenet portal patterns
 */

import { createContext, useContext, useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { useWallets } from "@privy-io/react-auth/solana";
import { apiClient } from "@/lib/axios";
import { queryClient } from "@/lib/queryClient";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: unknown;
  login: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    authenticated,
    ready,
    user,
    login,
    logout: privyLogout,
    getAccessToken,
  } = usePrivy();
  const { wallets } = useWallets();

  const [token, setToken] = useState<string | null>(null);

  const isAuthenticated = authenticated && ready;
  const isLoading = !ready;

  // Get the primary Solana wallet address
  const solanaAddress =
    wallets && wallets.length > 0 ? wallets[0].address : null;

  // Fetch and sync auth token
  useEffect(() => {
    const syncToken = async () => {
      if (isAuthenticated) {
        try {
          const accessToken = await getAccessToken();
          setToken(accessToken);
        } catch (error) {
          console.error("[Auth] Failed to get access token:", error);
          setToken(null);
        }
      } else {
        setToken(null);
      }
    };

    syncToken();
  }, [isAuthenticated, getAccessToken]);

  // Sync token to axios headers
  useEffect(() => {
    // Add delay to handle race conditions during auth flow
    const timeoutId = setTimeout(() => {
      if (isAuthenticated && token) {
        // Set Authorization header with Bearer token
        apiClient.defaults.headers.common["x-privy-access-token"] = token;
        queryClient.invalidateQueries();
      } else {
        // Clear auth headers when logged out
        delete apiClient.defaults.headers.common["x-privy-access-token"];

        // Invalidate all queries when user logs out
        if (!isAuthenticated && !isLoading) {
          queryClient.invalidateQueries();
        }
      }
    }, 100); // Small delay to avoid race conditions

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, token, solanaAddress, wallets, isLoading]);

  const handleLogout = async () => {
    await privyLogout();
    // Clear all cached data
    queryClient.clear();
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout: handleLogout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return context;
}
